/**
 * RESPONSIBILITY: draw the Contact page and send what it collects.
 *
 * The QUESTIONS are content: every tick-box and dropdown below is drawn from a
 * @StringList on the section, so the church changes what it asks without a deploy. Only
 * the shape of the form lives here.
 *
 * It posts to the app's own endpoint. The framework has no opinion about where a
 * submission goes, which is why createApp takes appRoutes.
 */
import { useState } from 'react';
import type { ContactSection } from '@app/sections';
import { APP_ROUTES } from '../../app-routes';
import { PageHead } from '../commons/PageHead';
import { Crumbs } from '../commons/Crumbs';
import { useWords } from '../commons/words';

type State = 'editing' | 'sending' | 'sent' | 'failed';

export interface ContactProps {
  c: ContactSection;
}

export function Contact({ c }: ContactProps) {
  const w = useWords();
  const [state, setState] = useState<State>('editing');
  const [wants, setWants] = useState<string[]>([]);

  const toggle = (v: string) =>
    setWants(wants.includes(v) ? wants.filter((x) => x !== v) : [...wants, v]);

  async function submit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setState('sending');

    const form = new FormData(ev.currentTarget);
    const res = await fetch(APP_ROUTES.contact, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...Object.fromEntries(form), wants }),
    }).catch(() => null);

    setState(res?.ok ? 'sent' : 'failed');
  }

  if (state === 'sent') {
    return (
      <main>
        <Crumbs trail={[{ label: w.aboutLabel }, { label: w.contactLabel }]} />
        <section className="band band-centre">
          <div className="wrap">
            <h2>{c.thanksHeading}</h2>
            <p className="lede">{c.thanks}</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <Crumbs trail={[{ label: w.aboutLabel }, { label: w.contactLabel }]} />
      <PageHead eyebrow={c.eyebrow} heading={c.heading} intro={c.intro} />

      <section className="band">
        <div className="wrap split">
          <form className="form" onSubmit={submit}>
            <div className="frow">
              <label className="field"><span>{c.firstNameLabel}</span>
                <input name="firstName" autoComplete="given-name" required /></label>
              <label className="field"><span>{c.lastNameLabel}</span>
                <input name="lastName" autoComplete="family-name" /></label>
            </div>

            <div className="frow">
              <label className="field"><span>{c.emailLabel}</span>
                <input type="email" name="email" autoComplete="email" required /></label>
              <label className="field"><span>{c.phoneLabel}</span>
                <input type="tel" name="phone" autoComplete="tel" /></label>
            </div>

            {c.describesYou?.length > 0 && (
              <fieldset>
                <legend>{c.describesYouQuestion}</legend>
                <div className="checks">
                  {c.describesYou.map((o) => (
                    <label className="check" key={o}>
                      <input type="radio" name="describesYou" value={o} />{o}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {c.iWouldLikeTo?.length > 0 && (
              <fieldset>
                <legend>{c.iWouldLikeToQuestion}</legend>
                <div className="checks">
                  {c.iWouldLikeTo.map((o) => (
                    <label className="check" key={o}>
                      <input type="checkbox" checked={wants.includes(o)} onChange={() => toggle(o)} />{o}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="frow">
              {c.heardAboutUs?.length > 0 && (
                <label className="field"><span>{c.heardAboutUsQuestion}</span>
                  <select name="heardAboutUs">
                    {c.heardAboutUs.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </label>
              )}
              {c.replyBy?.length > 0 && (
                <label className="field"><span>{c.replyByQuestion}</span>
                  <select name="replyBy">
                    {c.replyBy.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </label>
              )}
            </div>

            <label className="field"><span>{c.messageLabel}</span>
              <textarea name="message" rows={5}
                placeholder={c.messagePlaceholder} /></label>

            {state === 'failed' && (
              <p className="form-error">{c.failedMessage}</p>
            )}

            <div className="btns">
              <button className="btn" type="submit" disabled={state === 'sending'}>
                {state === 'sending' ? c.sendingLabel : c.sendLabel}
              </button>
            </div>
          </form>

          <aside className="contactinfo">
            {/* The page's own details, so the page's own pencil edits them — see the
                Contact section. They were on the footer, which never shows them. */}
            {c.email && <div><h4>{c.emailHeading}</h4><p>{c.email}</p></div>}
            {c.phone && <div><h4>{c.phoneHeading}</h4><p>{c.phone}</p></div>}
            {c.prayerEmail && <div><h4>{c.prayerHeading}</h4><p>{c.prayerEmail}</p></div>}
            {c.visitLabel && (
              <div className="btns"><a className="btn btn-ghost" href={c.visitHref || '#'}>{c.visitLabel}</a></div>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
}
